import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useId,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';

/** Deduplicate while keeping first-seen order */
function uniqueOptionStrings(input) {
  if (!Array.isArray(input)) return [];
  const seen = new Set();
  const out = [];
  for (const x of input) {
    if (x == null || x === '') continue;
    const s = String(x);
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

/**
 * DPR-style single-select. Menu is portaled to `document.body` with fixed
 * position so it never stacks under other table rows (avoids "nested" UI).
 */
const TechnicianLogCustomSelect = ({
  value,
  options = [],
  onChange,
  disabled = false,
  placeholder = 'Select…',
  showSearch = true,
  id: idProp,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [panelStyle, setPanelStyle] = useState({ top: 0, left: 0, width: 280 });
  const containerRef = useRef(null);
  const panelRef = useRef(null);
  const genId = useId();
  const listboxId = idProp || `dd-${String(genId).replace(/\W/g, '')}`;

  const opts = useMemo(() => uniqueOptionStrings(options), [options]);

  const filtered = useMemo(() => {
    if (!showSearch || !search.trim()) return opts;
    const q = search.trim().toLowerCase();
    return opts.filter((o) => o.toLowerCase().includes(q));
  }, [opts, search, showSearch]);

  const updatePanelPosition = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPanelStyle({
      top: r.bottom + 4,
      left: r.left,
      width: Math.max(200, r.width),
    });
  }, []);

  useLayoutEffect(() => {
    if (open) {
      updatePanelPosition();
    }
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => updatePanelPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updatePanelPosition]);

  const isInside = useCallback((node) => {
    if (!node) return false;
    if (containerRef.current && containerRef.current.contains(node)) return true;
    if (panelRef.current && panelRef.current.contains(node)) return true;
    return false;
  }, []);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!open) return;
      if (isInside(e.target)) return;
      setOpen(false);
      setSearch('');
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open, isInside]);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const display = value != null && value !== '' ? String(value) : '';

  const showSearchRow = showSearch && opts.length > 4;

  const panel = open && !disabled
    ? (
      <div
        ref={panelRef}
        className="modern-dsr-tech-dd__panel modern-dsr-tech-dd__panel--portal"
        id={`${listboxId}-listbox`}
        role="listbox"
        style={{
          position: 'fixed',
          top: panelStyle.top,
          left: panelStyle.left,
          width: panelStyle.width,
          zIndex: 10050,
        }}
      >
        {showSearchRow && (
          <div className="modern-dsr-tech-dd__search">
            <Search size={14} className="modern-dsr-tech-dd__search-icon" color="#9CA3AF" aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              placeholder="Search…"
              className="modern-dsr-tech-dd__search-input"
              autoComplete="off"
              aria-label="Filter options"
            />
          </div>
        )}
        <div className="modern-dsr-tech-dd__list">
          {filtered.length === 0 && (
            <div className="modern-dsr-tech-dd__empty">No matches</div>
          )}
          {filtered.map((opt, idx) => (
            <div
              key={`${opt}-${idx}`}
              role="option"
              aria-selected={value === opt}
              className={`modern-dsr-tech-dd__opt${value === opt ? ' modern-dsr-tech-dd__opt--active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange(opt);
                setOpen(false);
                setSearch('');
              }}
            >
              {opt}
            </div>
          ))}
        </div>
      </div>
    )
    : null;

  return (
    <div
      ref={containerRef}
      className="modern-dsr-tech-dd"
      data-open={open ? 'true' : 'false'}
      data-disabled={disabled ? 'true' : 'false'}
    >
      <button
        type="button"
        className="modern-dsr-tech-dd__trigger"
        onClick={() => {
          if (!disabled) {
            setOpen((o) => !o);
          }
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${listboxId}-listbox`}
      >
        <span
          className={display ? 'modern-dsr-tech-dd__value' : 'modern-dsr-tech-dd__value modern-dsr-tech-dd__value--placeholder'}
          title={display || placeholder}
        >
          {display || placeholder}
        </span>
        {open
          ? <ChevronUp size={16} color="#6B7280" className="modern-dsr-tech-dd__chev" />
          : <ChevronDown size={16} color="#6B7280" className="modern-dsr-tech-dd__chev" />
        }
      </button>
      {typeof document !== 'undefined' && createPortal(panel, document.body)}
    </div>
  );
};

export default TechnicianLogCustomSelect;
